import { computed, nextTick, ref, watch } from 'vue';
import { useStorage, watchDeep } from '@vueuse/core';
import { isEqual } from 'lodash-es';
import moment from 'moment';
import { storeToRefs } from 'pinia';
import type {
  AdditionalFee,
  AdoptionFee,
  AdoptionFeePayload,
  CartAnimal,
  CartVoucher,
  EuthanasiaFeePayload,
  RtoFee,
  RtoFeePayload,
} from '../../../../interfaces';
import type {
  FieldInitialSettings,
  FieldSchema,
  RawField,
  FileUpload,
  Field,
  OutcomeVoucherOption,
  OutcomeVoucherRow,
} from '@FormModule';
import type { GenericField, OId } from '@Types';
import type { Moment } from 'moment';
import { useAToast } from '@Atoms';
import { getShelterNow, PAYLOAD_DATE_FORMAT } from '@Constants';
import { getPayloadDateTimeString } from '@Helpers';
import { useSessionStore } from '@Stores';
import { useForm, base64ToFile, fileToBase64 } from '@FormModule';
import { OUTCOMES_WITH_VOUCHERS } from '../../../../constants';
import { OUTCOMES_TYPES } from '../../../../enums';
import { parseCartDateMoment } from '../../../../helpers/parseCartDate.helper';
import {
  RemoveOutcomeTempFileService,
  UpdateAdditionalFeesService,
  UpdateAllAnimalsRtoFeeOnOutcomeDateService,
  UpdateAnimalCartAdoptionFee,
  UpdateAnimalCartEuthanasiaFeeService,
  UpdateRtoFeeService,
  UploadOutcomeTempFileService,
} from '../../../../services';
import useShoppingCartStore from '../../../../store';
import getSanitizedPayload from './getSanitizedPayload';
import useFieldsOutcomeDate from './useFieldsOutcomeDate';

function getFeeIdString(id: OId | string | undefined): string {
  if (!id) return '';
  if (typeof id === 'string') return id;

  return id.$oid || '';
}

function matchesFeeId(feeId: OId | string | undefined, targetId: string): boolean {
  if (!feeId || !targetId) return false;
  if (typeof feeId === 'string') return feeId === targetId;
  return feeId.$oid === targetId;
}

function isSameCalendarDay(oldVal: unknown, newVal: unknown): boolean {
  if (oldVal == null || newVal == null) return false;

  const oldMoment = moment(oldVal);
  const newMoment = moment(newVal);
  if (!oldMoment.isValid() || !newMoment.isValid()) return false;

  return oldMoment.clone().startOf('day').isSame(newMoment.clone().startOf('day'), 'day');
}

export default function () {
  const { shelterProfile } = storeToRefs(useSessionStore());
  const {
    animals,
    cartData,
    hasCart,
    hasWaivers,
    isAdoption,
    isEuthanasia,
    isIntake,
    isReturnToOwner,
    loading,
    outcomeInfoActiveStep,
    outcomeType,
    selectedRtoFee,
  } = storeToRefs(useShoppingCartStore());

  const { fieldsSchema: outcomeDateFieldSchema } = useFieldsOutcomeDate();

  const { notify, TOAST_TYPES } = useAToast();
  const { getDetailsSchema } = useForm();

  const NOT_ALLOWED_FIELD = ['outcome_type'];
  const TOAST_ERROR = {
    message: 'cart.toast.outcomeInfoUpdate.error.message',
    title: 'cart.toast.outcomeInfoUpdate.error.title',
    type: TOAST_TYPES.ERROR,
  };

  const fieldsSettings: FieldInitialSettings = {
    additional_fees: {
      model: [],
      edit: {
        label: 'label',
        options: [],
      },
      settings: {
        maxSelectedLabels: 1,
        disableFilterAutofocus: true,
      },
    },
    adoption_fee: {
      model: '',
      edit: {
        value: '_id.$oid',
        label: 'label',
        options: [],
      },
      settings: {
        show: true,
      },
    },
    euthanasia_fee: {
      model: '',
      edit: {
        value: '_id',
        label: 'label',
        options: [],
      },
      settings: {
        show: true,
      },
    },
    file: {
      settings: {
        imageOnly: false,
        multiple: true,
        fileLimit: 5,
      },
    },
    partner_name: {
      edit: {
        label: 'name',
        value: 'id',
        options: [],
      },
    },
    rto_fee_type: {
      edit: {
        label: 'label',
        value: 'value',
        options: [
          {
            label: 'Daily',
            value: 'daily',
          },
          {
            label: 'Flat',
            value: 'flat',
          },
        ],
      },
    },
    rto_fees: {
      model: '',
      edit: {
        label: 'label',
        options: [''],
      },
      settings: {
        placeholder: 'Select a Fee Type first',
      },
      state: {
        disabled: true,
        errors: new Set(),
      },
    },
    additional_rto_fees: {
      model: [],
      edit: {},
      settings: {
        placeholder: 'components.forms.placeholder.description',
      },
    },
    waivers: {
      model: [],
      edit: {
        label: 'title',
        options: [],
      },
      settings: {
        maxSelectedLabels: 1,
        show: shelterProfile.value?.enable_outcome_waivers,
      },
    },
    vouchers: {
      model: [],
      edit: {
        label: 'title',
        options: [],
      },
      settings: {
        outcomeDate: undefined,
        preselectedIds: [],
        show: shelterProfile.value?.enable_outcome_vouchers,
      },
    },
  };

  const uploadedFiles = useStorage<FileUpload>('cart-uploaded-files', {});
  const alreadyUploadedFiles = useStorage<Set<string>>('cart-files-ids', new Set());

  const additionalFeesOptions = ref();
  const adoptionFeesOptions = ref<AdoptionFee[]>();
  const currentDataOnStepperId = ref('');
  const currentSchemaModels = ref<Set<GenericField>>(new Set());
  const euthanasiaFeesOptions = ref();
  const fieldsSchema = ref<FieldSchema[]>([]);
  const formError = ref();
  const isProgrammaticRtoFeeUpdate = ref(false);
  const isStepChanging = ref(false);
  const outcomeSchemaModels = ref<any[]>([]);
  const rawFields = ref<RawField[]>([]);
  const sourceData = ref<any | null>(null);
  const waivers = ref([]);
  const animalOutcomeVouchers = ref<Record<string, string[]>>({});
  const voucherTemplates = ref<OutcomeVoucherOption[]>([]);

  const additionalFeesModel = computed(() => fieldsSchema.value.find(({ name }) => name === 'additional_fees')?.model);
  const adoptionFee = computed(() => fieldsSchema.value.find(({ name }) => name === 'adoption_fee')?.model);
  const euthanasiaFee = computed(() => fieldsSchema.value.find(({ name }) => name === 'euthanasia_fee')?.model);
  const filesModel = computed(() => fieldsSchema.value.find(({ settings }) => settings.type === 'file')?.model || []);
  const formData = computed(() => animals.value.find((data: any) => data._id === currentDataOnStepperId.value));
  const rtoFeeField = computed(() => fieldsSchema.value.find(({ name }) => name === 'rto_fees')?.model);
  const rtoFeeTypeField = computed(() => fieldsSchema.value.find(({ name }) => name === 'rto_fee_type')?.model);
  const waiversModel = computed(() => fieldsSchema.value.find(({ name }) => name === 'waivers')?.model);

  const additionalFeesBlurState = computed(
    () => fieldsSchema.value.find(({ name }) => name === 'additional_fees')?.state.blur
  );

  const additionalFeesWatchSource = computed(() => ({
    model: additionalFeesModel.value,
    blur: additionalFeesBlurState.value ?? false,
  }));

  const outcomeDateModel = computed(
    () => outcomeDateFieldSchema.value.find(({ name }) => name === 'outcome_date')?.model
  );

  watch(adoptionFee, onAdoptionFeeUpdate);
  watch(adoptionFeesOptions, (newValue, oldValue) => setNewSpeciesAdoptionFeeOptions(newValue, oldValue));
  watch(currentDataOnStepperId, () => onStepUpdate());
  watch(euthanasiaFee, onEuthanasiaFeeUpdate);
  watch(euthanasiaFeesOptions, (newValue, oldValue) => setNewSpeciesEuthanasiaFeeOptions(newValue, oldValue));
  watch(filesModel, (newValue, oldValue) => onFilesModelUpdate(newValue, oldValue));
  watch(formData, () => setAdditionalFeeField(additionalFeesOptions.value));
  watch(outcomeDateModel, (newVal, oldVal) => onOutcomeDateUpdate(newVal, oldVal));
  watch(outcomeDateModel, onVouchersOutcomeDateUpdate);
  watch(rtoFeeField, () => onRtoFeesUpdate());
  watch(rtoFeeTypeField, (newVal, oldVal) => onRtoFeeTypeUpdate(newVal, oldVal));
  watchDeep(additionalFeesWatchSource, (newValue, oldValue) => handleAdditionalFeesChange(newValue, oldValue));
  watchDeep(waiversModel, onWaiversUpdate);

  // SETS
  function setForm() {
    if (!sourceData.value || !formData.value) return;

    rawFields.value = getRawFields(sourceData.value.rawFields);
    setFieldsSchema();

    //  NOTE: We need to to update the rto fee on setForm because the user can change the date and refresh the page
    // since now we are getting the outcome date from the cart data, we need to check every time to match the rto fee with the new date
    if (outcomeType.value === OUTCOMES_TYPES.RETURN_TO_OWNER) updateRtoFeeCalculationService();
  }

  function setFieldsSchema() {
    fieldsSchema.value = getUpdatedSchemaModel();
  }

  function setNewSpeciesAdoptionFeeOptions(newValue?: AdoptionFee[], oldValue?: AdoptionFee[]) {
    if (!isAdoption.value) return;

    fieldsSchema.value.forEach((field) => {
      if (field.name !== 'adoption_fee') return;

      const isSameSpecies = isEqual(newValue, oldValue);
      if (isSameSpecies) return;

      const adoptionFeeValue = formData.value?.adoption_fee_id ?? formData.value?.adoption_fee;
      if (field.edit) field.edit.options = newValue;

      field.model = '';
      field.value = '';

      if (!adoptionFeeValue) return;

      // Match by ID first, then amount+description, then amount only
      const matchingFee =
        newValue?.find((fee: AdoptionFee) => matchesFeeId(fee._id, adoptionFeeValue)) ??
        newValue?.find(
          (fee: AdoptionFee) =>
            parseFloat(String(fee.amount)) === parseFloat(String(formData.value?.adoption_fee)) &&
            fee.description === formData.value?.fee_name
        ) ??
        newValue?.find(
          (fee: AdoptionFee) => parseFloat(String(fee.amount)) === parseFloat(String(formData.value?.adoption_fee))
        );

      const feeId = getFeeIdString(matchingFee?._id);

      field.model = feeId;
      field.value = feeId;
    });
  }

  function setNewSpeciesEuthanasiaFeeOptions(newValue: any, oldValue: any) {
    if (!isEuthanasia.value || !isIntake.value) return;

    fieldsSchema.value.forEach((field) => {
      if (field.name !== 'euthanasia_fee') return;

      const isSameSpecies = isEqual(newValue, oldValue);
      if (isSameSpecies) return;

      const euthanasiaFeeAmount = formData.value?.euthanasia_fee;
      if (field.edit) field.edit.options = newValue;

      field.model = '';
      field.value = '';

      const hasEuthanasiaFee = euthanasiaFeeAmount && euthanasiaFeeAmount !== '0.00';
      if (hasEuthanasiaFee) {
        const matchingFee = newValue?.find(
          (fee: any) => fee.amount === euthanasiaFeeAmount || parseFloat(fee.amount) === parseFloat(euthanasiaFeeAmount)
        );
        const feeId = matchingFee?._id?.$oid || matchingFee?._id || '';
        field.model = feeId;
        field.value = feeId;
      }
    });
  }

  function setWaverField() {
    const waverFields = [
      {
        label: 'Outcome Waivers',
        name: 'waivers',
        required: false,
        type: 'multiselect',
        value: '',
      },
    ];

    fieldsSettings.waivers.edit!.options = waivers?.value;

    const index = rawFields.value.findIndex((field) => field.name === 'rto_fees');
    if (index && index !== -1) return rawFields.value.splice(index + 1, 0, ...waverFields);

    rawFields.value.unshift(...waverFields);
  }

  function setVouchersField() {
    const VOUCHERS_FIELD = {
      label: 'Outcome Vouchers',
      name: 'vouchers',
      required: false,
      type: 'outcome_vouchers',
      value: '',
    };

    fieldsSettings.vouchers.edit!.options = voucherTemplates.value;

    const waiversIndex = rawFields.value.findIndex((field) => field.name === 'waivers');
    if (waiversIndex !== -1) return rawFields.value.splice(waiversIndex + 1, 0, VOUCHERS_FIELD);

    const rtoFeesIndex = rawFields.value.findIndex((field) => field.name === 'rto_fees');
    if (rtoFeesIndex !== -1) return rawFields.value.splice(rtoFeesIndex + 1, 0, VOUCHERS_FIELD);

    rawFields.value.unshift(VOUCHERS_FIELD);
  }

  function setAdoptionFeeField() {
    rawFields.value.unshift({
      label: 'Adoption Fee',
      name: 'adoption_fee',
      required: true,
      type: 'select',
      value: '',
    });
  }

  function setEuthanasiaFeeField() {
    rawFields.value.unshift({
      label: 'Euthanasia Fee',
      name: 'euthanasia_fee',
      required: true,
      type: 'select',
      value: '',
    });
  }

  function setAdditionalFeeField(options?: AdditionalFee[]) {
    if (!options) return;

    const animal = animals.value.find((animal: any) => animal._id === currentDataOnStepperId.value);

    const optionsPerSpecies = options
      .filter((option) => option.species === animal?.species.toLowerCase())
      .sort((a, b) => a.description.localeCompare(b.description));

    if (!optionsPerSpecies.length) return;

    additionalFeesOptions.value = options;

    const newField = {
      label: 'Additional Fee(s)',
      name: 'additional_fees',
      required: false,
      type: 'multiselect',
      value: [],
    };

    const existingIndex = rawFields.value.findIndex((field) => field.name === 'additional_fees');
    if (existingIndex !== -1) {
      rawFields.value[existingIndex] = newField;
    } else {
      rawFields.value.push(newField);
    }

    const optionsWithCustomLabel = optionsPerSpecies.map((fee: AdditionalFee) => {
      return {
        ...fee,
        label: `${fee.description} - $${fee.amount}`,
      };
    });

    fieldsSettings.additional_fees.edit!.options = optionsWithCustomLabel;
  }

  function setRtoFeesField() {
    const rtoFields = [
      {
        label: 'RTO Fee Type',
        name: 'rto_fee_type',
        required: true,
        type: 'select',
        value: '',
      },
      {
        label: 'RTO Fee(s)',
        name: 'rto_fees',
        required: true,
        disabled: true,
        type: 'select',
        value: '',
      },
      {
        label: 'cart.label.customAdditionalFees',
        name: 'additional_rto_fees',
        required: false,
        type: 'custom_additional_fee',
        value: [],
      },
    ];

    rawFields.value.unshift(...rtoFields);
  }

  /** Adds only the Additional RTO fee field (e.g. for adoption form). */
  function setAdditionalRtoFeeFieldOnly() {
    const existingIndex = rawFields.value.findIndex((field) => field.name === 'additional_rto_fees');
    if (existingIndex !== -1) return;

    rawFields.value.push({
      label: 'cart.label.customAdditionalFees',
      name: 'additional_rto_fees',
      required: false,
      type: 'custom_additional_fee',
      value: [],
    });
  }

  async function uploadOutcomeTempFile(files: File[]) {
    if (!filesModel.value || !filesModel.value.length) return;

    const animalId = formData.value?.animal_id;
    if (!animalId) return;

    const filesToUpload = files.filter((file: File) => !alreadyUploadedFiles.value.has(`${animalId}-${file.name}`));
    if (!filesToUpload.length) return;

    async function updateFilesModel(file: any) {
      const base64Data = await fileToBase64(file);
      const fileRecord = {
        name: file.name,
        type: file.type,
        size: file.size,
        data: base64Data,
      };

      if (!uploadedFiles.value[animalId]) uploadedFiles.value[animalId] = [];
      uploadedFiles.value[animalId].push(fileRecord);
    }

    const TOAST_ERROR = {
      message: 'cart.toast.outcomeInfoUpdate.error.message',
      title: 'cart.toast.outcomeInfoUpdate.error.title',
      type: TOAST_TYPES.ERROR,
    };

    const TOAST_SUCCESS = {
      title: 'components.fileUploader.toast.title.success',
      type: TOAST_TYPES.SUCCESS,
    };

    const uploadPromises = filesToUpload.map(async (file: File) => {
      return await UploadOutcomeTempFileService(file, animalId).then(async () => {
        alreadyUploadedFiles.value.add(`${animalId}-${file.name}`);
        await updateFilesModel(file);
      });
    });

    await Promise.all(uploadPromises)
      .then(() => notify(TOAST_SUCCESS))
      .catch(() => notify(TOAST_ERROR));
  }

  function onFilesModelUpdate(newValue: any, oldValue: any) {
    if (isStepChanging.value) return;

    const newValueArray = Array.isArray(newValue) ? newValue : [];
    const oldValueArray = Array.isArray(oldValue) ? oldValue : [];

    if (newValueArray.length > oldValueArray.length) {
      uploadOutcomeTempFile(newValueArray);
    } else {
      removeOutcomeTempFile(newValueArray, oldValueArray);
    }
  }

  function removeOutcomeTempFile(newValue: any, oldValue: any) {
    const filesToRemove = oldValue.filter((file: any) => !newValue.includes(file));
    if (!filesToRemove.length) return;

    const animalId = formData.value?.animal_id;
    if (!animalId) return;

    const fileToRemove = filesToRemove[0];
    const payload = {
      animalId,
      filename: fileToRemove.name,
    };

    RemoveOutcomeTempFileService(payload).then(async () => {
      alreadyUploadedFiles.value.delete(`${animalId}-${fileToRemove.name}`);

      if (uploadedFiles.value[animalId]) {
        uploadedFiles.value[animalId] = uploadedFiles.value[animalId].filter(
          (fileRecord: any) => fileRecord.name !== fileToRemove.name
        );
      }
    });
  }

  // GETTERS
  function getUpdatedSchemaModel() {
    const fieldSchema = getDetailsSchema(rawFields.value, fieldsSettings, formData.value);

    fieldSchema.forEach((field) => {
      //prettier-ignore
      switch (field.name) {
        case 'adoption_fee': return setAdoptionFee(field)
        case 'euthanasia_fee': return setEuthanasiaFee(field)
        case 'additional_fees': return setAdditionalFeeField(field);
        case 'additional_rto_fees': {
          if (field.settings) {
            const rawId = formData.value?.animal_id ?? formData.value?._id;
            field.settings.animalId =
              typeof rawId === 'string' ? rawId : (rawId && '$oid' in rawId ? (rawId as { $oid: string }).$oid : '');
          }
          return setMatch(field);
        }
        case 'rto_fees': return setRtoFeesField(field);
        case 'waivers': return setWaiversField(field);
        case 'vouchers': return setVouchersModel(field);
        default: setMatch(field)
      }
    });

    async function setAdoptionFee(field: Field) {
      const adoptionFeeValue = formData.value?.adoption_fee_id ?? formData.value?.adoption_fee;

      if (field.edit) field.edit.options = adoptionFeesOptions.value;

      field.model = '';
      field.value = '';

      if (!adoptionFeeValue) return;

      // NOTE :: Match by ID first, then amount+description, then amount only
      const matchingFee =
        adoptionFeesOptions.value?.find((fee: AdoptionFee) => matchesFeeId(fee._id, adoptionFeeValue)) ??
        adoptionFeesOptions.value?.find(
          (fee: AdoptionFee) =>
            parseFloat(String(fee.amount)) === parseFloat(String(formData.value?.adoption_fee)) &&
            fee.description === formData.value?.fee_name
        ) ??
        adoptionFeesOptions.value?.find(
          (fee: AdoptionFee) => parseFloat(String(fee.amount)) === parseFloat(String(formData.value?.adoption_fee))
        );

      const feeId = getFeeIdString(matchingFee?._id);

      field.model = feeId;
      field.value = feeId;
    }

    function setRtoFeesField(field: Field) {
      const rtoFeeTypeValue = rtoFeeTypeField.value ?? formData.value?.rto_fee_type;
      const hasRtoFeeType = !!rtoFeeTypeValue;
      field.state.disabled = !hasRtoFeeType;

      const hasFeeTypeAndRtoFees = hasRtoFeeType && sourceData.value?.rto_fees?.length;
      if (hasFeeTypeAndRtoFees) {
        const animal = animals.value.find((animal: CartAnimal) => animal._id === currentDataOnStepperId.value);

        if (animal?.species) {
          const options = sourceData.value.rto_fees
            .filter(
              (fee: any) =>
                fee.type === String(rtoFeeTypeValue).toLowerCase() &&
                fee.species?.toLowerCase() === animal.species?.toLowerCase()
            )
            .map((fee: RtoFee) => ({
              ...fee,
              label: `${fee.description} - $${fee.amount}`,
            }));

          field.edit!.options = options;
          field.settings.placeholder = 'Select a RTO Fee';
        }
      }

      // NOTE: formData stores base_rto_fee (number), not rto_fees. Match by rto_fee_id first, then selectedRtoFee, then base_rto_fee.
      const stored = selectedRtoFee.value[outcomeInfoActiveStep.value];
      const storedMatchesType = stored && String(stored.type).toLowerCase() === String(rtoFeeTypeValue).toLowerCase();
      const options = field.edit?.options as RtoFee[] | undefined;
      const selectedFee =
        (storedMatchesType ? stored : null) ??
        (formData.value?.rto_fee_id && options?.length
          ? options.find((fee) => matchesFeeId(fee._id, formData.value!.rto_fee_id!))
          : undefined) ??
        options?.find((fee) => parseFloat(String(formData.value?.base_rto_fee)) === parseFloat(String(fee.amount)));

      isProgrammaticRtoFeeUpdate.value = true;

      field.model = selectedFee ?? '';
      field.value = selectedFee ?? '';

      nextTick(() => (isProgrammaticRtoFeeUpdate.value = false));
    }

    function setEuthanasiaFee(field: Field) {
      const euthanasiaFeeAmount = formData.value?.euthanasia_fee;

      if (field.edit) field.edit.options = euthanasiaFeesOptions.value;

      field.model = '';
      field.value = '';

      const hasEuthanasiaFee = euthanasiaFeeAmount && euthanasiaFeeAmount !== '0.00';
      if (hasEuthanasiaFee) {
        const matchingFee = euthanasiaFeesOptions.value?.find(
          (fee: any) => fee.amount === euthanasiaFeeAmount || parseFloat(fee.amount) === parseFloat(euthanasiaFeeAmount)
        );
        const feeId = matchingFee?._id?.$oid || matchingFee?._id || '';
        field.model = feeId;
        field.value = feeId;
      }
    }

    function setAdditionalFeeField(field: Field) {
      const selectedFees = formData.value?.additional_fees;
      const feesOptions = sourceData.value?.additional_fees_options;
      const missingParameters = !selectedFees || !selectedFees.length || !feesOptions || !feesOptions.length;
      if (missingParameters) return;

      const additionalFees = feesOptions
        .filter((fee: AdditionalFee) => selectedFees.some((selectedFee: any) => selectedFee._id === fee._id))
        .map((fee: AdditionalFee) => {
          return {
            ...fee,
            label: `${fee.description} - $${fee.amount}`,
          };
        });

      field.model = additionalFees;
      field.value = additionalFees;
    }

    function setFileField(field: Field) {
      const storedFiles = uploadedFiles.value[formData.value.animal_id];
      if (!storedFiles) {
        field.model = [];
        field.value = [];
        return;
      }

      // Convert stored base64 data back to File objects with objectURL
      const files = storedFiles.map((fileRecord) => {
        const file = base64ToFile(fileRecord.data, fileRecord.name, fileRecord.type);
        return file;
      });

      field.model = files;
      field.value = files;
    }

    function setWaiversField(field: Field) {
      if (!formData.value?.waivers) return;

      const selectedIds = formData.value[field.name].map((item: any) => item._id);

      const filteredWaivers = waivers?.value.filter((waiver: any) => selectedIds.includes(waiver._id));
      field.value = filteredWaivers;
      field.model = filteredWaivers;
    }

    // NOTE :: Rows already on the cart line win (staff confirmed them). A line with no `vouchers`
    // yet is pre-filled from the animal profile; the field builds those rows and their dates.
    function setVouchersModel(field: Field) {
      field.settings.outcomeDate = outcomeDateModel.value;

      const cartVouchers: CartVoucher[] | undefined = formData.value?.vouchers;
      if (Array.isArray(cartVouchers)) {
        const rows = getVoucherRowsFromCart(cartVouchers);

        field.settings.preselectedIds = [];
        field.value = rows;
        field.model = rows;
        return;
      }

      const animalId = String(formData.value?.animal_id ?? formData.value?._id ?? '');

      field.settings.preselectedIds = animalOutcomeVouchers.value[animalId] || [];
      field.value = [];
      field.model = [];
    }

    function setMatch(field: Field) {
      if (!formData.value) return;

      if (field.settings.type === 'file') {
        setFileField(field);
        return;
      }

      field.value = formData.value[field.name] || formData.value.outcome?.[field.name];
      field.model = formData.value[field.name] || formData.value.outcome?.[field.name];
    }

    return fieldSchema;
  }

  function getRawFields(dynamicFields: RawField[]) {
    const hasAdditionalFeeFields = sourceData.value?.additional_fees_options.length;
    if (hasAdditionalFeeFields && hasCart.value) setAdditionalFeeField(sourceData.value.additional_fees_options);

    const hasPartnerFields = sourceData.value?.partners.length;
    if (hasPartnerFields) fieldsSettings.partner_name.edit!.options = sourceData.value.partners;

    const hasRtoFees = sourceData.value?.rto_fees?.length;
    if (hasRtoFees) setRtoFeesField();

    const hasWaivers =
      isAdoption.value ||
      isReturnToOwner.value ||
      outcomeType.value === OUTCOMES_TYPES.CLINIC_OUT ||
      outcomeType.value === OUTCOMES_TYPES.SERVICE_OUT ||
      outcomeType.value === OUTCOMES_TYPES.TRANSFER_OUT;

    if (hasWaivers && waivers?.value && waivers?.value?.length) setWaverField();

    const canIssueVouchers =
      !!shelterProfile.value?.enable_outcome_vouchers &&
      OUTCOMES_WITH_VOUCHERS.includes(outcomeType.value || '') &&
      !!voucherTemplates.value.length;

    if (canIssueVouchers) setVouchersField();

    if (isAdoption.value) {
      setAdoptionFeeField();
      setAdditionalRtoFeeFieldOnly();
    }

    if (isEuthanasia.value && isIntake.value) setEuthanasiaFeeField();

    const sanitizedFields = [...rawFields.value, ...dynamicFields].filter(
      ({ name }) => !NOT_ALLOWED_FIELD.includes(name)
    );

    return sanitizedFields;
  }

  function getRtoFeesUpdatePayload() {
    if (!rtoFeeTypeField.value) return;

    const animalId: undefined | string = formData.value?.animal_id;
    const intakeMoment = parseCartDateMoment(formData.value?.intake_date);
    if (!animalId || !intakeMoment) return;

    // NOTE : If used somewhere else, this could be promoted to a helper
    const newLos: number = getLOS(intakeMoment);

    function getLOS(intakeDate: Moment): number {
      const outcomeDateModel = outcomeDateFieldSchema.value[0].model;
      const outcomeDate: Moment = moment(outcomeDateModel || getShelterNow()).startOf('day');
      const losDays: number = outcomeDate.diff(intakeDate.clone().startOf('day'), 'days', true);

      return Math.ceil(losDays + 1);
    }

    const newRtoFee = getRtoFee(newLos);
    function getRtoFee(los: number) {
      if (!rtoFeeField.value) return '';

      const rtoIsDaily = rtoFeeTypeField.value === 'daily';
      const amount = Math.round(parseFloat(rtoFeeField.value.amount) * 100) / 100;

      return rtoIsDaily ? amount * los : amount;
    }

    const payload: RtoFeePayload = {
      base_rto_fee: rtoFeeField.value?.amount ?? '',
      id: animalId,
      newLos,
      newRtoFee,
      rto_fee_type: rtoFeeTypeField.value,
      fee_name: rtoFeeField.value?.description,
    };

    if (shelterProfile.value?.enable_shopping_cart && rtoFeeField.value?._id) {
      const rtoFeeId = getFeeIdString(rtoFeeField.value._id);

      if (rtoFeeId) payload.rto_fee_id = rtoFeeId;
    }

    return payload;
  }

  // HELPERS
  function getVoucherRowsFromCart(cartVouchers: CartVoucher[]): OutcomeVoucherRow[] {
    return cartVouchers
      .map((cartVoucher) => {
        const template = voucherTemplates.value.find(({ _id }) => _id === cartVoucher.voucher_template_id);
        if (!template) return null;

        return {
          // NOTE :: The api stores the wire format, parsed as a local wall-clock date so the picker
          // shows the same day.
          exp_date: cartVoucher.exp_date ? moment(cartVoucher.exp_date, PAYLOAD_DATE_FORMAT).toDate() : null,
          no_expiration: !cartVoucher.exp_date,
          title: template.title,
          touched: true,
          valid_days: template.valid_days ?? null,
          voucher_template_id: template._id,
        };
      })
      .filter((row): row is OutcomeVoucherRow => !!row);
  }

  function updateAnimalFieldsOnStepUpdate() {
    const currentAnimalIndex = animals.value.findIndex((animal: any) => animal._id === currentDataOnStepperId.value);

    const currentAnimalModels = outcomeSchemaModels.value?.length
      ? outcomeSchemaModels.value[currentAnimalIndex]
      : null;

    const currentOutcomeFields = currentAnimalModels ? getSanitizedPayload(currentAnimalModels) : null;

    // NOTE :: Shallow update for the target animal
    cartData.value.animals[currentAnimalIndex] = {
      ...cartData.value.animals[currentAnimalIndex],
      ...currentOutcomeFields,
    };
  }

  // EVENTS
  function onVouchersOutcomeDateUpdate() {
    const vouchersField = fieldsSchema.value.find(({ name }) => name === 'vouchers');
    if (!vouchersField) return;

    vouchersField.settings.outcomeDate = outcomeDateModel.value;
  }

  function onWaiversUpdate() {
    hasWaivers.value = !!(waiversModel.value && waiversModel.value.length);
  }

  function onStepUpdate() {
    isStepChanging.value = true;
    updateAnimalFieldsOnStepUpdate();
    fieldsSchema.value = [];
    setFieldsSchema();
    onRtoFeeTypeUpdate();

    setTimeout(() => {
      isStepChanging.value = false;
    }, 100);
  }

  function onRtoFeeTypeUpdate(newVal?: unknown, oldVal?: unknown) {
    if (!rtoFeeTypeField.value) return;

    const rtoFields = sourceData.value?.rto_fees;
    if (!rtoFields?.length) return;

    const typeChangedByUser = oldVal !== undefined && newVal !== oldVal;
    if (typeChangedByUser) {
      selectedRtoFee.value[outcomeInfoActiveStep.value] = undefined as unknown as RtoFee;
    }

    updateRtoFeeOptions();

    function updateRtoFeeOptions() {
      const rtoFeesField = fieldsSchema.value.find(({ name }) => name === 'rto_fees');
      if (!rtoFeesField) return;

      if (!rtoFeesField?.edit) return;
      const animal = animals.value.find((animal: any) => animal._id === currentDataOnStepperId.value);

      const options: RtoFee[] = rtoFields
        .filter(
          (fee: any) =>
            fee.type === rtoFeeTypeField.value.toLowerCase() &&
            fee.species.toLowerCase() === animal.species.toLowerCase()
        )
        .map((fee: RtoFee) => {
          return {
            ...fee,
            label: `${fee.description} - $${fee.amount}`,
          };
        });

      rtoFeesField.state.disabled = false;
      rtoFeesField.edit.options = options;
      rtoFeesField.settings.placeholder = 'Select a RTO Fee';

      const rtoFee = typeChangedByUser ? undefined : getSelectedRtoFee();

      function getSelectedRtoFee() {
        if (selectedRtoFee.value[outcomeInfoActiveStep.value]) {
          return selectedRtoFee.value[outcomeInfoActiveStep.value];
        }

        const cartTypeMatchesCurrent = formData.value?.rto_fee_type === rtoFeeTypeField.value;
        if (!cartTypeMatchesCurrent) return undefined;

        const rtoFeeId = formData.value?.rto_fee_id;
        if (rtoFeeId && options?.length) {
          const matched = options.find((fee) => matchesFeeId(fee._id, rtoFeeId));
          if (matched) {
            return {
              ...matched,
              label: `${matched.description} - $${matched.amount}`,
            };
          }
        }

        const rtoFeeAmount =
          formData.value?.base_rto_fee || (formData.value && parseFloat(formData.value.base_rto_fee));

        if (!rtoFeeAmount) return undefined;

        const selectedRtoFeeData = options?.find(
          (fee) => parseFloat(String(rtoFeeAmount)) === parseFloat(String(fee.amount))
        );

        if (!selectedRtoFeeData) return undefined;

        return {
          ...selectedRtoFeeData,
          label: `${selectedRtoFeeData.description} - $${selectedRtoFeeData.amount}`,
        };
      }

      // NOTE: This setTimeout is necessary so the cart summary hava time to update its values when
      // going back and forth in the outcome information page
      isProgrammaticRtoFeeUpdate.value = true;
      setTimeout(() => {
        rtoFeesField.model = rtoFee ?? '';
        rtoFeesField.value = rtoFee ?? '';
        setTimeout(() => (isProgrammaticRtoFeeUpdate.value = false), 0);
      }, 50);
    }

    // NOTE : If we start to store all fee information within the outcome this should be reviewed
    const noUpdate = formData.value?.rto_fee_type === rtoFeeTypeField.value;
    if (noUpdate || loading.value) return;

    const PAYLOAD = getRtoFeesUpdatePayload();
    if (!PAYLOAD) return;

    PAYLOAD.base_rto_fee = '';
    PAYLOAD.newRtoFee = 0;
    PAYLOAD.fee_name = '';
    delete PAYLOAD.rto_fee_id;

    fieldsSchema.value.forEach((field) => {
      if (field.name !== 'rto_fees') return;

      field.model = '';
      field.value = '';
      field.state.errors = new Set();
    });

    loading.value = true;

    UpdateRtoFeeService(PAYLOAD)
      .then((data) => (cartData.value = data))
      .catch(() => notify(TOAST_ERROR))
      .finally(() => (loading.value = false));
  }

  function onOutcomeDateUpdate(newVal?: unknown, oldVal?: unknown) {
    if (loading.value || isProgrammaticRtoFeeUpdate.value) return;
    if (!isReturnToOwner.value) return;
    if (oldVal === undefined) return;

    const timeOnlyChange = isSameCalendarDay(newVal, oldVal);
    if (timeOnlyChange) return;

    updateRtoFeeCalculationService();
  }

  function updateRtoFeeCalculationService() {
    const outcomeDateModelValue = outcomeDateFieldSchema.value[0]?.model;
    if (!outcomeDateModelValue) return;

    const outcomeDateValue = getPayloadDateTimeString(outcomeDateModelValue);
    if (!outcomeDateValue) return;

    loading.value = true;

    UpdateAllAnimalsRtoFeeOnOutcomeDateService(outcomeDateValue)
      .then((data) => (cartData.value = data))
      .catch(() => notify(TOAST_ERROR))
      .finally(() => {
        loading.value = false;

        // NOTE :: The auto-filled fee's watcher fired while loading was true, so re-run it once the
        // recalc settles — otherwise the rto_fee_id backfill is skipped on first load. It no-ops when
        // the cart already holds the selected fee's id.
        onRtoFeesUpdate();
      });
  }

  function onRtoFeesUpdate() {
    if (loading.value) return;

    // NOTE :: A programmatic clear must not send an update — the fee-type change path sends its own
    if (!rtoFeeField.value) return;

    // NOTE :: This also fires on the programmatic auto-fill (not just a user reselect) so the backend
    // receives rto_fee_id and can resolve the fee's account_code_id. Same-price fees exist, so skip
    // only when the cart already holds both the same amount AND the same fee id.
    const sameAmount = parseFloat(formData.value?.base_rto_fee) === parseFloat(rtoFeeField.value?.amount);
    const sameFeeId =
      !!formData.value?.rto_fee_id && matchesFeeId(rtoFeeField.value?._id, getFeeIdString(formData.value.rto_fee_id));
    const noUpdate = sameAmount && sameFeeId;
    if (noUpdate) return;

    const PAYLOAD = getRtoFeesUpdatePayload();
    if (!PAYLOAD) return;

    // NOTE: We use this selectedRtoFee state to repopulate the select RTO Fees field after hard refresh
    if (rtoFeeField.value) {
      selectedRtoFee.value[outcomeInfoActiveStep.value] = {
        ...rtoFeeField.value,
        label: `${rtoFeeField.value.description} - $${rtoFeeField.value.amount}`,
      };
    }

    loading.value = true;

    UpdateRtoFeeService(PAYLOAD)
      .then((data) => (cartData.value = data))
      .catch(() => notify(TOAST_ERROR))
      .finally(() => (loading.value = false));
  }

  function onAdoptionFeeUpdate() {
    const adoptionFeeId: string = adoptionFee.value;
    if (!adoptionFeeId) return;

    const animalId: undefined | string = formData.value?.animal_id;
    if (!animalId) return;

    const selectedFee = adoptionFeesOptions.value?.find((fee: AdoptionFee) => matchesFeeId(fee._id, adoptionFeeId));

    const adoptionFeeAmount = String(selectedFee?.amount ?? '0.00');

    const PAYLOAD: AdoptionFeePayload = {
      adoption_fee: adoptionFeeAmount,
      animal_id: animalId,
      fee_name: selectedFee?.description,
    };

    if (shelterProfile.value?.enable_shopping_cart && selectedFee?._id) {
      PAYLOAD.adoption_fee_id = getFeeIdString(selectedFee._id);
    }

    UpdateAnimalCartAdoptionFee(PAYLOAD)
      .then((data) => (cartData.value = data))
      .catch(() => notify(TOAST_ERROR));
  }

  function onEuthanasiaFeeUpdate() {
    const euthanasiaFeeId: string = euthanasiaFee.value;
    if (!euthanasiaFeeId) return;

    const animalId: undefined | string = formData.value?.animal_id;
    if (!animalId) return;

    const selectedFee = euthanasiaFeesOptions.value?.find(
      (fee: any) => fee._id?.$oid === euthanasiaFeeId || fee._id === euthanasiaFeeId
    );

    const euthanasiaFeeAmount = selectedFee?.amount || '0.00';

    const PAYLOAD: EuthanasiaFeePayload = {
      euthanasia_fee: euthanasiaFeeAmount,
      animal_id: animalId,
      fee_name: selectedFee?.description,
    };

    if (shelterProfile.value?.enable_shopping_cart && selectedFee?._id) {
      PAYLOAD.euthanasia_fee_id = getFeeIdString(selectedFee._id);
    }

    UpdateAnimalCartEuthanasiaFeeService(PAYLOAD)
      .then((data) => (cartData.value = data))
      .catch(() => notify(TOAST_ERROR));
  }

  function handleAdditionalFeesChange(
    newValue: { model: AdditionalFee[]; blur: boolean },
    oldValue?: { model: AdditionalFee[]; blur: boolean }
  ) {
    let blurTimeout: ReturnType<typeof setTimeout> | null = null;

    if (!newValue.blur) return;

    if (blurTimeout) clearTimeout(blurTimeout);
    blurTimeout = setTimeout(() => {
      onAdditionalFeesUpdate(oldValue?.model);
    }, 150);
  }

  function onAdditionalFeesUpdate(oldFees?: AdditionalFee[]) {
    if (!additionalFeesBlurState.value) return;

    const isInitalFormLoad = typeof oldFees === 'undefined';
    if (isInitalFormLoad) return;

    const additionalFee: AdditionalFee[] = fieldsSchema.value.find(({ name }) => name === 'additional_fees')?.model;
    const animalId: undefined | string = formData.value?.animal_id;

    if (!animalId) return;

    const PAYLOAD = {
      animals: {
        additional_fees: additionalFee,
        animal_id: animalId,
        outcome_field: true,
      },
    };

    UpdateAdditionalFeesService(PAYLOAD)
      .then(({ data }) => (cartData.value = data))
      .catch(() => notify(TOAST_ERROR));
  }

  return {
    additionalFeesOptions,
    adoptionFeesOptions,
    animalOutcomeVouchers,
    currentDataOnStepperId,
    currentSchemaModels,
    euthanasiaFeesOptions,
    fieldsSchema,
    formError,
    outcomeSchemaModels,
    rawFields,
    sourceData,
    voucherTemplates,
    waivers,

    setForm,
    setAdditionalFeeField,
  };
}
